import type { AnyAction } from '@reduxjs/toolkit';
import { usePrevious } from 'hooks/usePrevious';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchString } from 'store/storageunits/reducer';
import type {
  GetApprovedFilesRequestActionInterface,
  GetStorageUnitsRequestActionInterface,
  GetTemplatesRequestActionInterface,
} from 'store/storageunits/sagaActions';
import {
  getActiveFilters,
  getAdvancedFilters,
} from 'store/storageunits/selectors';

type ActionPayload = { parentId?: string } & (
  | GetStorageUnitsRequestActionInterface['payload']
  | GetTemplatesRequestActionInterface['payload']
  | GetApprovedFilesRequestActionInterface['payload']
);

export const useFetchViewStorageUnits = <T extends ActionPayload>(
  action: (p: T) => AnyAction,
  params: T,
) => {
  const dispatch = useDispatch();
  const filters = useSelector(getActiveFilters);
  const advancedFilters = useSelector(getAdvancedFilters);
  const prevParentId = usePrevious(params.parentId);

  useEffect(() => {
    // Reset searchstring when navigating between folders
    if (params.parentId !== prevParentId) {
      dispatch(setSearchString(''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.parentId]);

  useEffect(() => {
    if (!params.searchString || params.searchString === '') {
      dispatch(action(params));
      return;
    }

    const timer = setTimeout(() => {
      dispatch(action(params));
    }, 300);

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.parentId, params.searchString, filters, advancedFilters]);
};
